import React, { useState, useEffect, useMemo } from 'react'
import { ReactComponent as ChatDotsIcon } from '../../../assets/svg/ChatDotsIcon.svg'
import { ReactComponent as ChevronDownDuoIcon } from '../../../assets/svg/ChevronDownDuoIcon.svg'
import { format } from 'date-fns'
import { assignedLoadingList } from '../../../constants/StudentAnalyticsConstants'
import { LoadingContainer } from './CheckInWidget'
import { loadAssignedActionsForClassAsync, loadAssignedActionsForStudentAsync } from '../../../server/server'
import { useHasStudentInPath, useQuery } from '../../../utils/util'
import { useTranslation } from 'react-i18next'
import { ReactComponent as GreenCircleCheck } from '../../../assets/svg/GreenCircleCheck.svg'
import { ReactComponent as Users } from '../../../assets/svg/users.svg'
import InfoButton from '../../common/InfoButton'
import { useTourContext } from '../../../context/TourContext'

const SHOW_ROWS = 4

function AssignedActionWidget({ daysFilter }) {
    const [showDropdown, setShowDropdown] = useState(false)
    const { t } = useTranslation()
    const query = useQuery()
    const isStudentAnalytics = useHasStudentInPath()
    const currentId = query.get('id')
    const [limit, setLimit] = useState(SHOW_ROWS)
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState(assignedLoadingList)
    const [noData, setNoData] = useState(false)
    const showLoading = useMemo(() => loading || noData, [loading, noData])
    const [dropdownOptions, setDropdownOptions] = useState([t('all'), t('Assigned'), t('Complete')])
    const { tourOpen } = useTourContext()

    useEffect(() => {
        setLoading(true)

        loadData()
    }, [daysFilter, currentId])

    const loadData = async () => {
        const callFunction = isStudentAnalytics ? loadAssignedActionsForStudentAsync : loadAssignedActionsForClassAsync
        const resp = await callFunction(currentId, daysFilter.startDate, daysFilter.endDate)
        if (resp.status === 200) {
            setData(!!resp?.data?.length ? resp.data : assignedLoadingList)
            setLoading(false)
            setNoData(!resp?.data?.length)
        }
    }

    const loadClassData = () => {
        setLoading(false)
    }

    const handleShowMore = () => {
        if (limit === SHOW_ROWS) {
            setLimit(data?.length)
        } else {
            setLimit(SHOW_ROWS)
        }
    }
    return (
        <div className="bg-white rounded-[24px] p-6 mt-4" data-tour="assigned_actions">
            <div className=" flex flex-row items-center">
                <div className="flex flex-row items-center w-full">
                    <p className="text-[18px] leading-[25px] font-medium text-darkenPurple ">{t('Assigned Actions')}</p>
                    <div className="flex items-center justify-center rounded-full bg-[#413F5E14] h-6 px-[10px] ml-2">
                        <p className="text-[13px] leading-[18px] font-normal text-strongGray ">{t(daysFilter.label)}</p>
                    </div>
                    <div className="flex-1"></div>
                </div>
                <div className="flex flex-row items-center gap-[20px]">
                    <InfoButton
                        text={
                            isStudentAnalytics
                                ? t(
                                      'This section displays all assigned actions based on the student’s activities. You can view the status of each assignment to see if any action has been taken.'
                                  )
                                : t(
                                      `Here you'll find actions that have been assigned to students, including their current status (Assigned or Completed). Track who assigned the action, when it was assigned, and any comments or updates related to the action.`
                                  )
                        }
                    />
                </div>
            </div>
            <div className="relative">
                <DataContainer data={data} limit={limit} blur={showLoading} isStudentAnalytics={isStudentAnalytics} />
                {showLoading && !tourOpen && <LoadingContainer showEmptyData={noData} />}
            </div>
            <div className="flex flex-row justify-end w-full mt-4">
                {!loading && data?.length > SHOW_ROWS && (
                    <button
                        className="flex flex-row items-center justify-center gap-2 border border-[#ECEBF8] hover:border-darkenPurple transition rounded-full h-[34px] px-4 mt-4 bg-[#F8F8FB]"
                        onClick={() => handleShowMore()}
                        disabled={loading || noData}
                    >
                        <p className="text-darkenPurple text-[16px] leading-5 font-semibold">{limit > SHOW_ROWS ? t('Show less') : t('Show more')}</p>
                        <div className={`${limit > SHOW_ROWS ? 'rotate-180' : 'rotate-0'} transition`}>
                            <ChevronDownDuoIcon />
                        </div>
                    </button>
                )}
            </div>
        </div>
    )
}

export default AssignedActionWidget

const DataContainer = ({ data, limit, blur, isStudentAnalytics }) => {
    const { t } = useTranslation()
    const { tourOpen } = useTourContext()

    return (
        <div className={`w-full mt-6 ${blur && !tourOpen ? 'blur' : ''} `}>
            <table className="small-custom-table w-full">
                <thead>
                    <tr>
                        {!isStudentAnalytics && (
                            <th>
                                <p>{t('Student')}</p>
                            </th>
                        )}
                        <th>
                            <p>{t('Status')}</p>
                        </th>
                        <th>
                            <p className="whitespace-nowrap">{t('Assigned to')}</p>
                        </th>
                        <th>
                            <p className="whitespace-nowrap">{t('Assigned by')}</p>
                        </th>
                        <th>
                            <p>{t('Date')}</p>
                        </th>
                        <th>
                            <p>{t('Message')}</p>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {data?.slice(0, limit)?.map((item) => (
                        <tr>
                            {!isStudentAnalytics && (
                                <td>
                                    <p className="font-bold text-[14px] leading-5">
                                        {item?.student?.first_name} {item?.student?.last_name?.charAt(0)}.
                                    </p>
                                </td>
                            )}
                            <td>
                                <p className="font-bold text-[14px] leading-5">
                                    <StatusItem item={item} />
                                </p>
                            </td>
                            <td>
                                <p className="font-medium text-[14px] leading-5 text-darkenPurple">
                                    {item?.assignedTo?.first_name} {item.assignedTo?.last_name}
                                </p>
                            </td>
                            <td>
                                <p className="font-medium text-[14px] leading-5 text-darkenPurple">
                                    {item.assignedBy?.first_name} {item.assignedBy?.last_name}{' '}
                                </p>
                            </td>
                            <td>
                                <p className="font-medium text-[14px] leading-5 text-darkenPurple">{format(new Date(item.created_date), 'dd.MM.yyyy HH:mm')}</p>
                            </td>
                            <td>
                                <div className="flex flex-col bg-[#F8F8FB] p-2 rounded-[8px]  gap-[3px] ">
                                    {/* <ChatDotsIcon /> */}
                                    <p className=" font-medium text-[14px] leading-5 ">{item.actionMessage}</p>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

const StatusItem = ({ item = {} }) => {
    const { t } = useTranslation()

    const completed = item?.feedback && item?.feedback.length > 0 && item?.feedback[0].actionTakenBy

    const icon = useMemo(() => (completed ? <GreenCircleCheck /> : <Users />), [completed])
    const label = useMemo(() => (completed ? t('Completed') : t('Assigned')), [completed, t])
    const bgColor = useMemo(() => (completed ? '#6FCC874D' : '#ECECF2'), [completed])
    const textColor = useMemo(() => (completed ? '#2B793FDE' : '#77768D'), [completed])

    return (
        <div className={`w-[105px] h-[28px] items-center flex flex-row py-[2px] px-[4px] rounded-[100px] gap-[4px]`} style={{ backgroundColor: bgColor }}>
            {icon}
            <p className="text-[14px] font-medium leading-[18px]" style={{ color: textColor }}>
                {label}
            </p>
        </div>
    )
}
